'use client';

import { useTranslations } from 'next-intl';
import {
  Carousel,
  CarouselArrow,
  CarouselContent,
  CarouselIndicator,
  CarouselItem,
} from '../../../../../packages/ui/components/carousel';
import { FC, ReactNode } from 'react';
import { formatThousands } from '@wwe-portal/utils/format';
import { cn } from '@wwe-portal/ui/lib/lib';
import styles from './top3-carousel.module.css';
import { InfoTooltip } from '../../../../../packages/ui/components/tooltip/info-tooltip';

export const Top3Carousel: FC<{
  subtitle?: string;
  data: {
    name: string;
    more?: ReactNode;
    tooltip?: string;
    items: {
      id: string;
      avatar: ReactNode;
      title: ReactNode;
      subtitle?: ReactNode;
      points?: number;
    }[];
  }[];
}> = ({ data, subtitle }) => {
  const tRank = useTranslations('rank_order');
  return (
    <Carousel
      opts={{
        align: 'start',
      }}
      isFlooded
    >
      <CarouselContent className="-ml-4">
        {data.map((criteria) => (
          <CarouselItem
            key={criteria.name}
            className="3xl:basis-1/3 basis-[90%] pr-[5px] md:basis-1/2 xl:basis-1/2 2xl:basis-[40%]"
          >
            <div
              className={cn(
                'overflow-hidden rounded-xl',
                styles.carouselContent
              )}
            >
              <div className="bg-back400 p-5">
                <div className="">
                  <div className="flex items-center justify-between">
                    <div className="text-text400 flex gap-2 text-sm font-medium">
                      {criteria.name}
                      {criteria?.tooltip && (
                        <InfoTooltip content={criteria.tooltip} />
                      )}
                    </div>
                    {criteria.more}
                  </div>
                  <div className="text-text600 text-xs">{subtitle}</div>
                </div>
              </div>
              <div className="flex flex-col gap-3 p-5">
                {criteria.items.map((item, index) => (
                  <div
                    key={item.id}
                    className="border-borderColor12 bg-bgColor24 flex items-center justify-between rounded-xl border px-4 py-2.5"
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-9 w-9">
                        <div
                          className={cn(
                            'flex cursor-pointer items-center justify-center rounded-full',
                            styles.avatarContainer
                          )}
                        >
                          {item.avatar}
                        </div>
                      </div>
                      <div>
                        <p className="w-full text-xs font-semibold">
                          {item.title}
                        </p>
                        {subtitle && (
                          <p className="text-text400 hover:text-text300 text-xs font-semibold">
                            {item.subtitle}
                          </p>
                        )}
                        {item.points != null && (
                          <div className="text-text400 block text-xs md:hidden">
                            {formatThousands(item.points)}
                          </div>
                        )}
                      </div>
                    </div>
                    {item.points != null && (
                      <div className="text-text400 my-auto mr-5 hidden flex-1 items-center self-end text-right text-xs md:block">
                        {formatThousands(item.points)}
                      </div>
                    )}
                    <div
                      className={cn(
                        'bg-text300 flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold',
                        index === 0 && 'bg-[#ffcf00]',
                        index === 1 && 'bg-[#00baff]',
                        index === 2 && 'bg-green700'
                      )}
                    >
                      {tRank(String(index + 1))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </CarouselItem>
        ))}
      </CarouselContent>
      <CarouselIndicator className="mt-6 gap-0 md:hidden" />
      <CarouselArrow className="hidden md:block" />
    </Carousel>
  );
};
